import { Scanner } from '@gwsmedika/scanner-js'
import { FC, useCallback, useEffect, useRef, useState } from 'react'
import Spinner from 'react-bootstrap/Spinner'
import { useIntl } from 'react-intl'

import { NRICCardOCRAPI } from '@api/nricCardOcrAPI'

import { FileModel } from '@models/file'
import { NRICCardOCRModel } from '@models/nricCardOcr'

import { Button } from '@components/Button'
import { ErrorMessage } from '@components/ErrorMessage'

import {
  KTP_SCANNER_FILE_TRANSFER_SERVICE_PORT,
  KTP_SCANNER_IP,
  KTP_SCANNER_SCAN_SERVICE_PORT,
  KTP_SCANNER_SECURE_PROTOCOL,
} from '@/constants/constant'
import AxiosUtil from '@/utils/axiosUtil'

type Props = {
  onExtracted: (nricCard: NRICCardOCRModel, file: FileModel) => void
}

const NRICExtractor: FC<Props> = ({ onExtracted }) => {
  const intl = useIntl()
  const scanner = useRef(
    new Scanner(undefined, {
      ip: KTP_SCANNER_IP,
      scanServicePort: KTP_SCANNER_SCAN_SERVICE_PORT,
      fileTransferServicePort: KTP_SCANNER_FILE_TRANSFER_SERVICE_PORT,
      secure: KTP_SCANNER_SECURE_PROTOCOL,
    })
  ).current
  const [isScanning, setIsScanning] = useState<boolean>(false)
  const [isExtracting, setIsExtracting] = useState<boolean>(false)
  const [isPreparing, setIsPreparing] = useState<boolean>(false)
  const [error, setError] = useState<string>()
  const [deviceName, setDeviceName] = useState<string>()
  const isLoading = isScanning || isExtracting || isPreparing
  const isDisabled = isLoading || !deviceName

  const getDevices = useCallback(async () => {
    try {
      setIsPreparing(true)

      const { data } = await scanner.getDevices()

      const devName = data.find((name) => name === '7C8U')
      if (devName) {
        setDeviceName(devName)
      } else {
        setError(intl.formatMessage({ id: 'nric_scanner.no_devices' }))
      }
    } catch (err) {
      setError(intl.formatMessage({ id: 'nric_scanner.no_devices' }))
    } finally {
      setIsPreparing(false)
    }
  }, [intl, scanner])

  const extractNRIC = async (image: File) => {
    try {
      setIsExtracting(true)

      const { data } = await NRICCardOCRAPI.extract(image)

      if (data) {
        onExtracted(data.nric_card, data.file)
      }
    } catch (err) {
      if (AxiosUtil.isAxiosError(err)) {
        setError(err.response?.data.message)
      } else {
        setError(String(err))
      }
    } finally {
      setIsExtracting(false)
    }
  }

  const handleClick = async () => {
    try {
      setError(undefined)
      setIsScanning(true)

      if (deviceName) {
        const { data } = await scanner.scan(deviceName)

        const files = data.map((v) => new File([v.file], v.filename, { type: v.file.type }))

        if (files.length > 0) {
          extractNRIC(files[0])
        }
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      setError(err.message)
    } finally {
      setIsScanning(false)
    }
  }

  useEffect(() => {
    getDevices()
  }, [getDevices])

  return (
    <div>
      <div className="d-flex align-items-center gap-5">
        <Button
          disabled={isDisabled}
          theme="primary"
          onClick={handleClick}
        >
          {intl.formatMessage({ id: 'nric_scanner.button' })}
        </Button>

        {isLoading && (
          <div className="d-flex align-items-center gap-2 text-gray-700">
            <Spinner
              animation="border"
              size="sm"
            />
            {isPreparing && intl.formatMessage({ id: 'nric_scanner.get_devices' })}
            {isExtracting && intl.formatMessage({ id: 'nric_scanner.extracting' })}
            {isScanning && intl.formatMessage({ id: 'nric_scanner.scanning' })}
          </div>
        )}
      </div>
      <ErrorMessage message={error} />
    </div>
  )
}

export { NRICExtractor }
